import { useMutation, useQueryClient } from '@tanstack/react-query';

import { fetchClient } from '@/lib/openapi-fetch/fetchClient';
import { candidateSubmissionsAwaitingReviewQueryKeys } from '@/modules/hiring-manager/queries/useCandidateSubmissionsAwaitingReviewQuery';
import { endpoints } from '@/modules/jobs/constants';
import type { JobState } from '@/modules/jobs/jobStates';
import { jobsQueryKeys } from '@/modules/jobs/queries/queryKeys';

interface UpdateJobStateParams {
  jobId: string;
  state: JobState;
}

/**
 * `PUT /jobs/{id}/state`: pauses, resumes or closes a job. The API enforces
 * which moves are allowed; the updated job is written into the detail cache.
 */
export const useUpdateJobStateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ jobId, state }: UpdateJobStateParams) => {
      const { data } = await fetchClient.PUT(endpoints.jobState, {
        params: { path: { id: jobId } },
        body: { state },
      });
      return data;
    },
    onSuccess: (job, { jobId }) => {
      if (job) queryClient.setQueryData(jobsQueryKeys.detail(jobId), job);
    },
    onSettled: (_data, _error, { jobId }) => {
      void queryClient.invalidateQueries({
        queryKey: jobsQueryKeys.detail(jobId),
      });
      void queryClient.invalidateQueries({ queryKey: jobsQueryKeys.lists() });
      // Closing a job drops its candidates from "Needs your decision".
      void queryClient.invalidateQueries({
        queryKey: candidateSubmissionsAwaitingReviewQueryKeys.all,
      });
    },
  });
};
